import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import useSavedItemsStore from "../../../store/savedItemsStore";
import { auth, firestore } from "../../../firebase/firebase";
import { doc, getDoc, updateDoc, arrayRemove } from "firebase/firestore";
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Text,
  Image,
  Link,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { Helmet } from "react-helmet-async";

const MembersPage = () => {
  const [loading, setLoading] = useState(true);
  const savedArticles = useSavedItemsStore((state) => state.savedArticles);
  const savedBooks = useSavedItemsStore((state) => state.savedBooks);
  const setSavedArticles = useSavedItemsStore(
    (state) => state.setSavedArticles
  );
  const setSavedBooks = useSavedItemsStore((state) => state.setSavedBooks);

  const reversedSavedArticles = [...savedArticles].reverse();
  const reversedSavedBooks = [...savedBooks].reverse();

  const removeSavedArticle = useSavedItemsStore(
    (state) => state.removeSavedArticle
  );
  const removeSavedBook = useSavedItemsStore((state) => state.removeSavedBook);

  const handleRemoveArticle = async (article) => {
    const user = auth.currentUser;
    if (user) {
      removeSavedArticle(article.title);
      const userDocRef = doc(firestore, "users", user.uid);
      await updateDoc(userDocRef, {
        savedArticles: arrayRemove(article),
      });

      const updatedDoc = await getDoc(userDocRef);
      if (updatedDoc.exists()) {
        const data = updatedDoc.data();
        setSavedArticles(data.savedArticles || []);
      }
    }
  };

  const handleRemoveBook = async (book) => {
    const user = auth.currentUser;
    if (user) {
      removeSavedBook(book.title);
      const userDocRef = doc(firestore, "users", user.uid);
      await updateDoc(userDocRef, {
        savedBooks: arrayRemove(book),
      });

      const updatedDoc = await getDoc(userDocRef);
      if (updatedDoc.exists()) {
        const data = updatedDoc.data();
        setSavedBooks(data.savedBooks || []);
      }
    }
  };

  useEffect(() => {
    const loadSavedItems = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(firestore, "users", user.uid);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSavedArticles(data.savedArticles || []);
          setSavedBooks(data.savedBooks || []);
        }
        setLoading(false);
      }
    };
    loadSavedItems();
  }, [setSavedArticles, setSavedBooks]);

  return (
    <>
    <Helmet>
        <title>Members - The New York Clone</title>
    </Helmet>
      <Header />
      {loading ? (
        <Spinner mt={3} mx={{ base: "5", md: "10" }} size="xl" />
      ) : null}
      <Flex
        mt={3}
        mb={8}
        minH={1000}
        gap={5}
        mx={{ base: "5", md: "10" }}
        flexDir={"column"}
      >
        <Heading as={"h1"}>My favorites</Heading>
        <Heading fontSize={"24px"} as={"h2"}>
          Articles
        </Heading>
        {savedArticles.length === 0 ? (
          <Text as="i">
            Still haven't found any interesting article? Go search for them in
            the homepage.
          </Text>
        ) : (
          <>
            {reversedSavedArticles.map((item) => (
              <Stack
                direction={{ base: "column", md: "row" }}
                borderWidth={"1px"}
                borderRadius={"lg"}
                maxW={{ base: "100vw", lg: "70vw", xl: "60vw" }}
              >
                <Flex flexDir={{ base: "column", md: "row" }}>
                  <Box>
                    <Link href={item.url} target="_blank">
                      <Image
                        borderLeftRadius={"lg"}
                        borderRightRadius={{ base: "lg", md: "none" }}
                        borderBottomRadius={{ base: "none", md: "lg" }}
                        borderBottomEndRadius={{ base: "none", md: "none" }}
                        src={item.imageUrl}
                        alt={item.title}
                        w={{ base: "100vw", md: "35vw" }}
                      />
                    </Link>
                  </Box>
                  <Box
                    flex={1}
                    key={item.id}
                    mb={1}
                    p={3}
                    w={{ base: "100%", md: "50%" }}
                  >
                    <Link
                      href={item.url}
                      _hover={{ textDecoration: "none" }}
                      target="_blank"
                    >
                      <Heading size={"md"} _hover={{ color: "gray.600" }} mb={2}>
                        {item.title}
                      </Heading>
                    </Link>
                    <Text>{item.description}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {item.author}
                    </Text>
                  </Box>
                  <Box py={{ base: 1, md: 3 }}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        icon={<HiOutlineDotsHorizontal />}
                        variant="link"
                        color={"black"}
                      />
                      <MenuList placement="bottom-end">
                        <MenuItem
                          icon={<DeleteIcon />}
                          onClick={() => handleRemoveArticle(item)}
                        >
                          Delete article
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Box>
                </Flex>
              </Stack>
            ))}
          </>
        )}

        <Heading fontSize={"24px"} as={"h2"}>
          Books
        </Heading>
        {savedBooks.length === 0 ? (
          <Text as="i">
            Still haven't found any interesting book? Go search for them in the
            homepage.
          </Text>
        ) : (
          <>
            {reversedSavedBooks.map((item) => (
              <Stack
                direction={{ base: "column", md: "row" }}
                borderWidth={"1px"}
                borderRadius={"lg"}
                maxW={{ base: "100vw", md: "60vw" }}
              >
                <Flex flexDir={{ base: "column", md: "row" }} maxH={"100%"}>
                  <Flex justifyContent={{ base: "center", md: "start" }}>
                    <Link href={item.bookUrl} target="_blank">
                      <Image
                        borderLeftRadius={{ base: "none", md: "lg" }}
                        h={{ base: "40vh", lg: "50vh" }}
                        src={item.bookImage}
                        alt={item.title}
                      />
                    </Link>
                  </Flex>
                  <Box
                    flex={1}
                    key={item.id}
                    mb={1}
                    p={3}
                    w={{ base: "100%", md: "50%" }}
                  >
                    <Link
                      href={item.bookUrl}
                      _hover={{ textDecoration: "none" }}
                      target="_blank"
                    >
                      <Heading size={"md"} _hover={{ color: "gray.600" }}>
                        {item.title}
                      </Heading>
                    </Link>
                    <Text>{item.description}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {item.contributor}
                    </Text>
                  </Box>
                  <Box py={{ base: 1, md: 3 }}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        icon={<HiOutlineDotsHorizontal />}
                        variant="link"
                        color={"black"}
                      />
                      <MenuList>
                        <MenuItem
                          icon={<DeleteIcon />}
                          onClick={() => handleRemoveBook(item)}
                        >
                          Delete book
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Box>
                </Flex>
              </Stack>
            ))}
          </>
        )}
      </Flex>
      <Footer />
    </>
  );
};

export default MembersPage;
